// src/services/leadService.js

import axios from 'axios';

// Base URL for the API
const API_URL = process.env.REACT_APP_API_URL;

// Get the auth token from localStorage or authService
const getAuthToken = () => localStorage.getItem('token');

// Set the Authorization header with the token
const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`,
  },
});

// Fetch all leads
export const getLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads`, getHeaders());
      const leads = response.data.leads;

      // Ensure we return an array, even if no leads are returned
      const check = Array.isArray(leads) ? leads : [];
      return check;
    } catch (error) {
      console.error('Error fetching leads:', error);
      throw error;
    }
  };

// Get lead details by ID
export const getLeadById = async (id) => {
  try { 
    const response = await axios.get(`${API_URL}/leads/${id}`, getHeaders());
    return response;
  } catch (error) {
    console.error(`Error fetching lead ${id}:`, error);
    throw error; 
  } 
};

// Create a new lead
export const createLead = async (leadData) => {
  try {
    const response = await axios.post(`${API_URL}/leads`, leadData, getHeaders());
    return response;
  } catch (error) {
    console.error('Error creating lead:', error);
    throw error;
  }
};

// Update an existing lead
export const updateLead = async (id, leadData) => {
  try {
    const response = await axios.put(`${API_URL}/leads/${id}`, leadData, getHeaders());
    return response;
  } catch (error) {
    console.error(`Error updating lead ${id}:`, error);
    throw error;
  }
};

// Update lead status (all service requests)
export const updateLeadStatus = async (id, statusId) => {
  try {
    const response = await axios.put(`${API_URL}/leads/${id}/status`, { statusId }, getHeaders());
    return response;
  } catch (error) {
    console.error(`Error updating lead status ${id}:`, error);
    throw error;
  }
};

// Delete a lead
export const deleteLead = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/leads/${id}`, getHeaders());
    console.log("Deletando: ", response);
    return response;
  } catch (error) {
    console.error(`Error deleting lead ${id}:`, error);
    throw error;
  }
};

// Fetch all lead statuses
export const getLeadStatuses = async () => {
  try {
    const response = await axios.get(`${API_URL}/lead-statuses`, getHeaders());
    return response;
  } catch (error) {
    console.error('Error fetching lead statuses:', error);
    throw error;
  }
};

// Fetch all lead origins
export const getLeadOrigins = async () => {
  try {
    const response = await axios.get(`${API_URL}/lead-origins`, getHeaders());
    return response;
  } catch (error) {
    console.error('Error fetching lead origins:', error);
    throw error;
  }
};

// Fetch the Services
export const getLeadServices = async () => {
  try{
    const response = await axios.get(`${API_URL}/services`, getHeaders());
    return response;
  }catch(error){
    console.error('Error returning services', error);
    throw error;
  }
};

// Function to search leads based on query
export const searchLeads = async (query) => {
  try {
    const response = await axios.get(`${API_URL}/leads/search`, {
      params: { query },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`, // Include authorization token if necessary
      },
    });
    return response;
  } catch (error) {
    console.error('Error searching leads:', error);
    throw error;
  }
};

export const getLeadWithRelated = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/leads/${id}/details`, getHeaders());
    return response;
  } catch (error) {
    console.error(`Error fetching lead details ${id}:`, error);
    throw error;
  }
};

// Get scheduled jobs for a lead
export const getLeadJobs = async (id, params = {}) => {
  try {
    const response = await axios.get(
      `${API_URL}/leads/${id}/jobs`,
      {
        ...getHeaders(),
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          status: params.status
        }
      }
    );
    return response;
  } catch (error) {
    console.error(`Error fetching lead jobs ${id}:`, error);
    throw error;
  }
};

// Get estimates for a lead
export const getLeadEstimates = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/leads/${id}/estimates`, getHeaders());
    return response;
  } catch (error) {
    console.error(`Error fetching lead estimates ${id}:`, error);
    throw error;
  }
};

// Lead Notes (LeadLog) CRUD
export const getLeadNotes = async (leadId) => {
  const response = await axios.get(`${API_URL}/lead-logs/lead/${leadId}`, getHeaders());
  return response.data;
};

export const createLeadNote = async (data) => {
  const response = await axios.post(`${API_URL}/lead-logs`, data, getHeaders());
  return response.data;
};

export const updateLeadNote = async (id, data) => {
  const response = await axios.put(`${API_URL}/lead-logs/${id}`, data, getHeaders());
  return response.data;
};

export const deleteLeadNote = async (id) => {
  const response = await axios.delete(`${API_URL}/lead-logs/${id}`, getHeaders());
  return response.data;
};

// Pipeline API
export const getLeadsPipeline = async () => {
  try {
    const response = await axios.get(`${API_URL}/leads/pipeline`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error fetching pipeline:', error);
    throw error;
  }
};

export const updateLeadPipeline = async (id, stage) => {
  try {
    const response = await axios.put(`${API_URL}/leads/${id}/pipeline`, { stage }, getHeaders());
    return response.data;
  } catch (error) {
    console.error(`Error updating lead pipeline ${id}:`, error);
    throw error;
  }
};